<template>
	<div class="flex flex-col gap-2">
		<p class="text-md leading-[22px] font-semibold text-won-dark-blue">What is your ideal role?</p>

		<SearchInput
			v-model="roleInput"
			:is-selected="isInputRoleSelected"
			:is-disabled="isInputRoleDisabled"
			@update:model-value="updateRoleInputHandler"
			@confirm:model-value="confirmRoleInputHandler"
			@focus:model-value="focusRoleInputHandler"
		/>
	</div>

	<div class="flex flex-col gap-2">
		<p class="text-md leading-[22px] font-semibold text-won-dark-blue">Recommendations based on Primary Resume</p>
		<div v-if="isRecommendedTitlesLoading || !isParsingCompleted">
			<LoadingSpinner
				class="py-6"
				:size-in-px="38"
				:show-loading-text="false"
				:top-margin-in-px="0"
			/>
			<p
				v-if="!isParsingCompleted"
				class="mt-2 text-center text-base leading-[22px] font-medium text-won-green"
			>
				Currently analyzing your Primary Resume to give you our top 5 job title recommendations!
			</p>
		</div>
		<div
			v-else
			class="flex flex-col gap-2"
		>
			<RecommendationLabel
				v-if="isFetchingTitlesErrors"
				:is-success="false"
				:message="fetchingTitleErrorMessage"
			/>
			<div
				v-for="(option, index) in recommendedTitles"
				v-else
				:key="index"
				class="flex flex-col gap-2"
			>
				<button
					class="rounded-lg border px-3 py-2 text-left text-md leading-[22px] font-medium"
					:class="[
						selectedOption === option
							? 'border-won-green bg-won-green-hover text-won-green'
							: 'border-won-sand-light bg-won-sand-light',
					]"
					@click="selectOption(option)"
				>
					{{ option }}
				</button>
			</div>
			<button
				:disabled="!isConfirmButtonEnabled"
				class="
					rounded-lg bg-won-green px-6 py-2 text-base leading-5 font-bold
					text-white disabled:bg-won-silver-cloud-gray
				"
				@click="confirmSelectionHandler"
			>
				Confirm Selection
			</button>
		</div>
	</div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';

import { useJobTitleRecommenderStore } from '../../store/job-title-recommender.store';

import { selectionTypes, errorTypes, errorMessages } from '../../utils/job-title-recommender';
import RecommendationLabel from './recommendation-label.vue';
import LoadingSpinner from '../library/loading-spinner.vue';
import SearchInput from './search-input.vue';

const jobTitleRecommenderStore = useJobTitleRecommenderStore();

const {
	isRecommendedTitlesLoading,
	selectedOption,
	roleInput,
	isConfirmButtonEnabled,
	errors,
	isParsingCompleted,
	recommendedTitles,
} = storeToRefs(jobTitleRecommenderStore);

const { setSelectedType, getRecommendedTitles, confirmSelectionHandler } = jobTitleRecommenderStore;

const isInputRoleSelected = ref(false);
const isInputRoleDisabled = ref(false);

const isFetchingTitlesErrors = computed(() => {
	return (
		errors.value[errorTypes.FAIL_FETCHING_RECOMMENDED_TITLES] ||
		errors.value[errorTypes.FAIL_MISSING_RECOMMENDED_TITLES]
	);
});

const fetchingTitleErrorMessage = computed(() => {
	const MISSING = errorTypes.FAIL_MISSING_RECOMMENDED_TITLES;
	const FETCHING = errorTypes.FAIL_FETCHING_RECOMMENDED_TITLES;

	if (errors.value) {
		return MISSING in errors.value ? errors.value[MISSING] : errors.value[FETCHING];
	}

	return errorMessages[FETCHING];
});

const updateRoleInputHandler = () => {
	isInputRoleSelected.value = false;

	if (roleInput.value.length === 0) {
		selectedOption.value = null;

		
	}
};

const focusRoleInputHandler = () => {
	setSelectedType(selectionTypes.INPUT);

	selectedOption.value = null;
	isInputRoleDisabled.value = false;
};

const confirmRoleInputHandler = () => {
	if (roleInput.value) {
		isInputRoleSelected.value = true;
		selectedOption.value = null;

		setSelectedType(selectionTypes.INPUT);
	} else {
		isInputRoleSelected.value = false;
	}
};

const selectOption = (option) => {
	isInputRoleSelected.value = false;
	setSelectedType(selectionTypes.OPTION);

	if (roleInput.value) {
		isInputRoleDisabled.value = true;
	}

	if (option === selectedOption.value) {
		selectedOption.value = null;
		return;
	}

	selectedOption.value = option;
};

onMounted(async () => {
	if (roleInput.value) {
		isInputRoleSelected.value = true;
	}

	await getRecommendedTitles();
});
</script>
